<template>
  <div>
    <v-btn id="Key_addr_back_btn" icon @click="closeModal">
      <v-icon>chevron_left</v-icon>
    </v-btn>

    <v-row no-gutters align="center">
      <span class="text-h6 context_title_color--text">
        {{ $tc('derive_key') }}
      </span>

      <span class="text-h5 font-weight-medium mx-1">
        {{ '/' }}
      </span>

      <svg
        class="mr-2"
        style="width: 24px; height: 24px"
        viewBox="0 0 24 24"
      >
        <path :fill="iconcolor" :d="svg" />
      </svg>

      <card-toolbar :title="name" class="mt-md-2" />
    </v-row>

    <v-row no-gutters align="center">
      <v-col cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('type') }}
        </span>

        <p>
          {{ type }}
        </p>
      </v-col>

      <v-col v-if="size" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('size') }}
        </span>

        <p>
          {{ size }}
        </p>
      </v-col>

      <v-col v-if="kinfo" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('bchain_version') }}
        </span>

        <p>
          {{ this.$tc('yes') }}
        </p>

        <!-- <p>
          {{ kinfo.version === 1 ? this.$tc('yes') : this.$tc('no') }}
        </p> -->
      </v-col>

      <v-col v-if="kinfo" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('x_depth') }}
        </span>

        <p>
          {{ kinfo.x_depth }}
        </p>
      </v-col>

      <v-col v-if="kinfo" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('x_ver') }}
        </span>

        <p>
          {{ versionInfo(kinfo.x_ver) }}
        </p>
      </v-col>

      <v-col v-if="kinfo" cols="6" md="3">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('x_par_fp') }}
        </span>

        <p>
          {{ kinfo.x_par_fp }}
        </p>
      </v-col>

      <v-col v-if="kinfo && kinfo?.version === 1" cols="6">
        <span
          class="subtitle-1 font-weight-medium"
        >
          {{ this.$tc('x_child_num') }}
        </span>

        <p>
          <span>{{ parseInt(kinfo.x_child_num, 16) }}</span>
          <span>{{ '/' }}</span>
          <span class="subtitle-2 context_title_color--text">{{ '0x' + kinfo.x_child_num }}</span>
        </p>
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12">
        <v-autocomplete
          v-if="type !== 'BIP32 XPRIV'"
          id="blockchain_version"
          :label="$tc('version')"
          :items="versions"
          item-text="name"
          item-value="version"
          v-model="version"
        />

        <div v-else>
          <span
            class="subtitle-1 font-weight-medium"
          >
            {{ this.$tc('version') }}
          </span>

          <p
            id="blockchain_version_key"
          >
            {{ versionKey() }}
          </p>
        </div>
      </v-col>

      <v-col cols="12">
        <v-switch
          id="blockchain_exportable"
          class="mt-0"
          color="green"
          v-model="hardenedKey"
          :label="$tc('hardened_key')"
        />
      </v-col>

      <v-col cols="6" sm="3" md="2" class="text-center">
        <v-text-field
          id="blockchain_index"
          class="text_field mx-auto"
          :label="$tc('derive_index')"
          v-model="index"
          type="number"
          :error-messages="indexErrors"
          hide-spin-buttons
          append-outer-icon="add"
          @click:append-outer="incrementIndex"
          prepend-icon="remove"
          @click:prepend="decrementIndex"
          @input="$v.index.$touch()"
          @blur="$v.index.$touch()"
        />

        <span class="subtitle-2 context_title_color--text">{{ '0x' + decimalToHex(index) }}</span>
      </v-col>

      <v-col cols="12">
        <v-text-field
          id="blockchain_name"
          :label="$tc('name')"
          :hint="$tc('key_name_hint')"
          v-model="newName"
          :error-messages="nameErrors"
          counter
          @input="$v.newName.$touch()"
          @blur="$v.newName.$touch()"
        />
      </v-col>

      <v-col cols="6" md="2" class="pt-0 mt-0">
        <v-switch
          id="blockchain_exportable"
          class="mt-0"
          color="green"
          :label="$tc('exportable_key')"
          v-model="exportable"
        />
      </v-col>
    </v-row>

    <v-row no-gutters justify="end">
      <v-btn
        color="blue"
        :disabled="$v.$invalid"
        :dark="!$v.$invalid"
        @click="submit"
        id="KeysNewModal_bchain_submit"
      >
        {{ $t("derive_key") }}
      </v-btn>
    </v-row>
  </div>
</template>

<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators'
import CardToolbar from '@/components/CardToolbar'

const EXPORTABLE = 16387
const NOT_EXPORTABLE = 16386

const RANGE_NOT_HARDNED_MIN = 0
const RANGE_NOT_HARDNED_MAX = 2147483647

const RANGE_HARDNED_MIN = 2147483648
const RANGE_HARDNED_MAX = 4294967295

const XPRIV_TEST_NET = '04358394'
const XPRIV_MAIN_NET = '0488ADE4'

const FPRIV = '03273A10'
const FPUB = '0488b21e'

const HATHOR_MAIN_NET = '03523b05'
const HATHOR_TEST_NET = '0434c8c4'

export default {
  components: {
    CardToolbar
  },
  data () {
    return {
      name: null,
      svg: null,
      iconcolor: null,
      page: 1,
      data: null,
      newName: null,
      versions: [{ name: this.$tc('main_net'), version: 128 }, { name: this.$tc('test_net'), version: 239 }],
      version: null,
      exportable: false,
      index: RANGE_HARDNED_MIN,
      hardenedKey: true,
      info: null,
      kinfo: null,
      kinfoVersion: null
    }
  },
  async  created () {
    this.name = this.$route.params.name
    this.svg = this.$route.params.svg
    this.iconcolor = this.$route.params.iconcolor
    this.page = this.$route.params.page
    this.info = this.$route.params.info

    this.kinfo = await this.getKinfoVersion()
    this.kinfoVersion = this.kinfo.x_ver
  },
  computed: {
    nameErrors () {
      const errors = []
      if (!this.$v.newName.$dirty) return errors
      !this.$v.newName.required && errors.push(this.$t('$validation.required'))
      !this.$v.newName.minLength &&
        errors.push(
          this.$t('$validation.min_length', {
            num: this.$v.newName.$params.minLength.min
          })
        )
      !this.$v.newName.maxLength &&
        errors.push(
          this.$t('$validation.max_length', {
            num: this.$v.newName.$params.maxLength.max
          })
        )
      !this.$v.newName.keyExists && errors.push('Key exists')
      !this.$v.newName.validChars &&
        errors.push(this.$t('$validation.invalid_key_name'))
      return errors
    },
    indexErrors () {
      const errors = []

      if (!this.$v.index.$dirty) return errors
      !this.$v.index.required && errors.push(this.$t('$validation.required'))
      !this.$v.index.checkValue && errors.push(this.$t('$validation.index_out_range'))
      return errors
    },
    inputPolicy () {
      return this.$store.state.secPolicy
    },
    setAttr () {
      if (this.exportable) {
        return EXPORTABLE
      } else {
        return NOT_EXPORTABLE
      }
    },
    type () {
      return this.$store.getters['keys/getKeyType']({ type: this.info.type }).type
    },
    size () {
      return this.$store.getters['keys/getKeyType']({ type: this.info.type }).size
    }
  },
  validations () {
    const validation = {}

    const keyExists = value => {
      if (!value) return true
      const keys = this.$store.state.hsm.listObjects
      const find = keys.find(key => key.id === value)
      return find === undefined
    }

    const validChars = value => {
      if (!value) return true
      const numbeOfMatches = (value.match(/^([A-Za-z0-9]+\/)?[A-Za-z0-9_]+$/g))
      if (!numbeOfMatches) return false
      return numbeOfMatches.length === 1
    }

    const checkValue = (value) => {
      const index = parseInt(value)
      if (this.hardenedKey === true) {
        return index >= RANGE_HARDNED_MIN && index <= RANGE_HARDNED_MAX
      } else {
        return index >= RANGE_NOT_HARDNED_MIN && index <= RANGE_NOT_HARDNED_MAX
      }
    }

    validation.newName = {
      required,
      minLength: minLength(this.inputPolicy.key_name_min_len),
      maxLength: maxLength(this.inputPolicy.key_name_max_len),
      keyExists,
      validChars
    }

    validation.version = {
      required
    }

    validation.index = {
      required,
      checkValue
    }

    return validation
  },
  methods: {
    decimalToHex (decimal) {
      return decimal.toString(16)
    },
    versionInfo (version) {
      switch (version) {
        case XPRIV_TEST_NET:
        case HATHOR_TEST_NET:
          return this.$tc('test_net')

        case XPRIV_MAIN_NET:
        case HATHOR_MAIN_NET:
          return this.$tc('main_net')

        case FPRIV:
          return this.$tc('fpriv')

        case FPUB:
          return this.$tc('fpub')

        default:
          return version
      }
    },
    versionKey () {
      if (this.kinfoVersion !== XPRIV_TEST_NET) {
        this.version = this.versions[0].version
        return this.versions[0].name
      } else {
        this.version = this.versions[1].version
        return this.versions[1].name
      }
    },
    incrementIndex () {
      const maxRange = this.hardenedKey ? RANGE_HARDNED_MAX : RANGE_NOT_HARDNED_MAX

      if (this.index < maxRange) {
        this.index = parseInt(this.index, 10) + 1
      }
    },
    decrementIndex () {
      const minRange = this.hardenedKey ? RANGE_HARDNED_MIN : RANGE_NOT_HARDNED_MIN

      if (this.index > minRange) {
        this.index = parseInt(this.index, 10) - 1
      }
    },
    async getKinfoVersion () {
      const kinfo = await this.$store.dispatch('hsm/bchain_kinfo', {
        body: {
          key: this.name
        }
      })
        .catch((err) => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })

      return kinfo
    },
    closeModal () {
      this.$router.push(
        {
          name: 'partition-keys',
          query: {
            p: this.page
          }
        }
      )
    },
    submit () {
      this.$store.dispatch('hsm/bchain_bip32_xprv_derive', {
        body: {
          key: this.name,
          dst: this.newName,
          version: this.version,
          attr: this.setAttr,
          index: this.index
        }
      })
        .then((res) => {
          this.data = res
          this.$notify.success({})
        })
        .catch((err) => {
          const errObj = this.$store.getters['error/getErrorObj'](err)
          if (errObj) {
            this.$notify.hsmError(errObj, err)
          } else {
            this.$notify.error({ message: err })
          }
        })
        .finally(
          this.closeModal()
        )
    }
  },
  watch: {
    hardenedKey (val) {
      if (val !== true) {
        this.index = RANGE_NOT_HARDNED_MIN
      } else {
        this.index = RANGE_HARDNED_MIN
      }
    }
  }
}
</script>

<style lang="scss">
  .text_field{
    width: 260px;

    input{
      text-align: center;
    }
  }

</style>
